import { Component } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ClerkService, ClerkSignInComponent, SignInProps } from "ngx-clerk-iliad";
import { environment } from "../../../../../environments/environment";
import { AppUrlsService } from "../../../../services/app-urls.service";
import { BrandService } from "../../../../shared/branding/brand.service";

@Component({
  selector: "app-sign-in",
  imports: [ClerkSignInComponent],
  templateUrl: "./sign-in.component.html",
})
export class SignInComponent {
  public signInProps: SignInProps = {};

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly brandService: BrandService,
    private readonly appUrlsService: AppUrlsService,
    private readonly clerkService: ClerkService,
  ) {
    // If we've been redirected here from a satellite domain, all of its query parameters will be wrapped up behind
    // the URL's fragment, so pull those out and combine them with the real query params into a single lookup.
    // (Note: We substring(1) to chop off the leading slash otherwise it won't parse as a query string)
    const params = new URLSearchParams(this.activatedRoute.snapshot.fragment?.substring(1) ?? "");
    for (const key of this.activatedRoute.snapshot.queryParamMap.keys) {
      const value = this.activatedRoute.snapshot.queryParamMap.get(key);
      if (value !== null) {
        params.set(key, value);
      }
    }

    // The ClerkAuthGuardService calls Clerk.redirectToSignIn() and sets 'signInFallbackRedirectUrl' to
    // the user's target URL, which shows up as a query param in snake_case_form.
    // If the auth guard sent us here, pick that up, and
    // bundle it into the SignIn component, because it doesn't pick it up automatically.
    const userDestination = params.get("sign_in_fallback_redirect_url");
    const destString = userDestination !== null ? `?dest=${encodeURIComponent(userDestination)}` : "";

    // After sign-in, any user might conceivably be a partially-created user (e.g. their Connect account
    // isn't fully filled out with Clerk info yet). By default, redirect them to a page that will call the
    // Website API's completeSignIn method (which ensures that their account is all ready to go).
    //
    // A custom redirect_url query parameter can also be provided (for example for external SSO flows such as
    // RAN Expense), in which case the user is redirected there instead. The value is validated against an
    // allowlist of hostnames to prevent open-redirect abuse, since it ends up as forceRedirectUrl below.
    const rawRedirectUrl = params.get("redirect_url");
    const redirectUrl =
      rawRedirectUrl !== null && this.isAllowedRedirectUrl(rawRedirectUrl)
        ? rawRedirectUrl
        : `${this.appUrlsService.websiteUrl}/signInRedirect${destString}`;

    const brand = this.brandService.brand();

    const signInProps = {
      forceRedirectUrl: redirectUrl,
      signUpUrl: `${this.appUrlsService.websiteUrl}/signUp${destString}`,
      signUpForceRedirectUrl: `${this.appUrlsService.websiteUrl}/signUp${destString}`,
      withSignUp: true,
      appearance: {
        layout: {
          logoImageUrl: brand.logos.iconLogoLightMode,
        },
      },
      initialValues: {
        // If the user was sent here with an email query param, prefill it in the sign-in form
        // This is used in the invitation flow, which sends the user to ?email=x&lang=y
        emailAddress: params.get("email") ?? undefined,
      },
    };

    if (this.appUrlsService.currentRootDomain !== this.appUrlsService.primaryRootDomain) {
      // If we're on a satellite domain, redirect the user to the primary domain's sign-in page.
      this.clerkService.redirectToSignIn({
        initialValues: params.get("email")
          ? {
              emailAddress: params.get("email") ?? undefined,
            }
          : {},
        signInForceRedirectUrl: signInProps.forceRedirectUrl,
        signUpForceRedirectUrl: signInProps.signUpForceRedirectUrl,
      });
    } else {
      this.signInProps = signInProps;
    }
  }

  /**
   * Validates a user-supplied post-sign-in redirect target before it is handed to Clerk as
   * `forceRedirectUrl`. Without this check, the `redirect_url` query parameter would be an
   * open-redirect vector. A URL is accepted only when it parses, uses HTTPS, and points at either
   * the configured Connect website host or the Clerk Frontend API host for this environment.
   *
   * @param rawUrl The raw query-parameter value to validate.
   * @returns `true` if the URL is safe to redirect to, `false` otherwise.
   */
  private isAllowedRedirectUrl(rawUrl: string): boolean {
    let url: URL;
    try {
      url = new URL(rawUrl);
    } catch {
      return false;
    }
    if (url.protocol !== "https:") {
      return false;
    }
    const allowedHostnames = [
      ...this.appUrlsService.allowedOrigins.map((x) => new URL(x).hostname),
      this.getClerkFrontendApiDomain(),
    ];
    return allowedHostnames.includes(url.hostname);
  }

  /**
   * Derives the Clerk Frontend API domain for the current environment from the configured
   * publishable key. Clerk publishable keys have the form `pk_(test|live)_<base64Payload>`, where
   * the base64-decoded payload is `<frontendApiDomain>$` (the trailing `$` is a Clerk-internal
   * delimiter and is stripped here).
   *
   * @returns The Clerk Frontend API hostname (e.g. `clerk.connect.rantalainen.fi`).
   */
  private getClerkFrontendApiDomain(): string {
    const encoded = environment.clerk.publishableKey.split("_")[2];
    return atob(encoded).replace(/\$$/, "");
  }
}
